import { forwardRef, Ref, useState, ReactElement, ChangeEvent, useEffect } from 'react';
import {
  Avatar,
  Link,
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  lighten,
  List,
  ListItem,
  ListItemAvatar,
  TextField,
  Theme,
  Tooltip,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Hidden,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { TransitionProps } from '@mui/material/transitions';
import { NavLink as RouterLink } from 'react-router-dom';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ChevronRightTwoToneIcon from '@mui/icons-material/ChevronRightTwoTone';
import menuConfig, { menuItemModuleOn } from 'src/config/menu';
import { passesMenuAccessGate, getNavPermissionContext } from 'src/Common/permissions/cloudPosPermissions';
import { getAppointmentFlags } from 'src/Common/appointments/appointmentFlags';
import { useSettingsTokens } from 'src/content/applications/Settings/CompanySettings/settingsUi';


const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement<any, any> },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const DialogWrapper = styled(Dialog)(
  () => `
    .MuiDialog-container {
        height: auto;
    }
    
    .MuiDialog-paperScrollPaper {
        max-height: calc(100vh - 64px)
    }
`
);

const SearchInputWrapper = styled(TextField)(
  ({ theme }) => `
    background: ${theme.colors.alpha.white[100]};

    .MuiInputBase-input {
        font-size: ${theme.typography.pxToRem(17)};
    }
`
);

const DialogTitleWrapper = styled(DialogTitle)(
  ({ theme }) => `
    background: ${theme.colors.alpha.black[5]};
    padding: ${theme.spacing(3)}
`
);

function HeaderSearch() {
  const [openSearchResults, setOpenSearchResults] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [filteredResults, setFilteredResults] = useState<any[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const k = useSettingsTokens();
  // Visual-only switch: Full Modern keeps its search dialog; classic gets the calm new look.
  const isModern = Boolean((theme as any).isModern);
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    const storedRoles = localStorage.getItem('userRoles') || '';
    setRoles(storedRoles.split(',').map(role => role.trim()));
  }, []);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value.toLowerCase();
    setSearchValue(value);

    if (value) {
      const { lookup, bypass } = getNavPermissionContext();
      // Opt-in modules: the switch as this session knows it right now, checked outside the access
      // gate (owners pass the gate, but a switched-off module's item is never offered).
      const moduleFlags = getAppointmentFlags();
      const flatMenu = menuConfig.flatMap(group => group.items);
      const results = flatMenu.filter(item =>
        menuItemModuleOn(item, moduleFlags) &&
        passesMenuAccessGate(item, roles, lookup, bypass) &&
        (item.title.toLowerCase().includes(value) || item.path.toLowerCase().includes(value))
      );
      setFilteredResults(results);
      setOpenSearchResults(results.length > 0);
    } else {
      setOpenSearchResults(false);
      setFilteredResults([]);
    }
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchValue('');
    setFilteredResults([]);
  };

  return (
    <>
      <Tooltip arrow title="Search">
        <IconButton color="primary" onClick={handleClickOpen}>
          {isModern ? <SearchTwoToneIcon /> : <SearchRoundedIcon />}
        </IconButton>
      </Tooltip>

      <DialogWrapper
        open={open}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="md"
        fullWidth
        scroll="paper"
        onClose={handleClose}
        PaperProps={
          isModern
            ? undefined
            : {
                // Classic: the flat hairline surface (from the theme) in the chrome colour.
                sx: {
                  bgcolor: isDark ? k.navBg : k.pageBg,
                  backgroundImage: 'none',
                  '& .MuiDivider-root': { bgcolor: k.border }
                }
              }
        }
      >
        <DialogTitleWrapper sx={isModern ? undefined : { background: 'transparent', p: '16px' }}>
          <SearchInputWrapper
            value={searchValue}
            autoFocus={true}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchTwoToneIcon />
                </InputAdornment>
              )
            }}
            placeholder="Search terms here..."
            fullWidth
            label="Search"
            sx={
              isModern
                ? undefined
                : {
                    background: 'transparent',
                    '.MuiInputBase-input': { fontSize: 15 },
                    '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: k.muted, fontSize: 20 }
                  }
            }
          />
        </DialogTitleWrapper>
        <Divider />

        {openSearchResults && (
          <DialogContent>
            <Box
              sx={{ pt: 0, pb: 1 }}
              display="flex"
              justifyContent="space-between"
            >
              <Typography
                variant="body2"
                component="span"
                sx={isModern ? undefined : { color: k.muted, fontSize: 13 }}
              >
                Search results for{' '}
                <Typography
                  sx={isModern ? { fontWeight: 'bold' } : { fontWeight: 500, fontSize: 13, color: k.text }}
                  variant="body1"
                  component="span"
                >
                  {searchValue}
                </Typography>
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <List
              disablePadding
              sx={
                isModern
                  ? undefined
                  : {
                      // Classic: quiet result rows — a neutral icon tile, 14px title, muted path.
                      '& .MuiListItem-root': {
                        borderRadius: '8px',
                        px: '10px',
                        py: '8px',
                        '&:hover': { bgcolor: k.hover }
                      },
                      '& .MuiAvatar-root': {
                        width: 34,
                        height: 34,
                        borderRadius: '8px',
                        background: k.subtleBg,
                        color: k.navText,
                        border: `1px solid ${k.border}`,
                        '& .MuiSvgIcon-root': { fontSize: 18 }
                      },
                      '& .MuiLink-root': { fontWeight: 500, fontSize: 14, color: k.text },
                      '& .MuiListItem-root > .MuiBox-root > .MuiTypography-root': { color: k.muted, fontSize: 13 },
                      '& .MuiListItem-root > .MuiSvgIcon-root': { color: k.muted, fontSize: 18 },
                      '& .MuiDivider-root': { my: '2px', bgcolor: k.rowDivider }
                    }
              }
            >
              {filteredResults.map((item) => (
                <div key={item.path}>
                  <ListItem 
                    button 
                    component={RouterLink} 
                    to={item.path}
                    onClick={handleClose}
                  >
                    <Hidden smDown>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            background: (theme: Theme) =>
                              theme.palette.secondary.main
                          }}
                        >
                          {item.icon}
                        </Avatar>
                      </ListItemAvatar>
                    </Hidden>
                    <Box flex="1">
                      <Box display="flex" justifyContent="space-between">
                        <Link
                          component={RouterLink}
                          to={item.path}
                          underline="hover"
                          sx={{ fontWeight: 'bold' }}
                          variant="body2"
                        >
                          {item.title}
                        </Link>
                      </Box>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          color: (theme: Theme) =>
                            lighten(theme.palette.secondary.main, 0.5)
                        }}
                      >
                        {item.path}
                      </Typography>
                    </Box>
                    <ChevronRightTwoToneIcon />
                  </ListItem>
                  <Divider sx={{ my: 1 }} component="li" />
                </div>
              ))}
            </List>
            {filteredResults.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2">No results found</Typography>
              </Box>
            )}
          </DialogContent>
        )}
      </DialogWrapper>
    </>
  );
}

export default HeaderSearch;